import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  createUserMember,
  validateAccessLink,
  getTiposInstituicao,
  getAreasAtuacao,
  getCargos,
} from '../services/api.js';
import NotificationPopup from '../components/NotificationPopup.js';
import SignupSuccessModal from '../components/SignupSuccessModal.js';
import {
  isValidEmail,
  isValidPassword,
  getPasswordErrorMessage,
} from '../utils/validation.js';
import ReatorBg from '../assets/reator.png';
import { CircularProgress } from '@mui/material';

const InviteSignUpPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    institutionId: null,
    acceptedTerms: false,
    acceptedPrivacy: false,
    birthDate: '',
    gender: '',
    id_tipo_instituicao: '',
    id_area_atuacao: '',
    id_cargo: '',
  });
  const [tiposInstituicao, setTiposInstituicao] = useState([]);
  const [areasAtuacao, setAreasAtuacao] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: '',
  });
  const [successModal, setSuccessModal] = useState({ open: false, email: '' });

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const types = await getTiposInstituicao();
        setTiposInstituicao(Array.isArray(types) ? types : []);
      } catch (err) {
        console.error('Error fetching institution types:', err);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setNotification({
          show: true,
          message:
            'Acesso negado. É necessário utilizar um link de convite válido para acessar esta página.',
          type: 'error',
        });
        window.setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const data = await validateAccessLink(token);
        if (data.valido) {
          setFormData(prev => ({
            ...prev,
            email: data.nm_email_usuario_invite,
            institutionId: data.id_instituicao,
          }));
          setValidating(false);
          setLoading(false);
        } else {
          setNotification({
            show: true,
            message:
              data.motivo ||
              'Este link de convite é inválido ou já expirou. Por favor, verifique ou solicite um novo convite.',
            type: 'error',
          });
          window.setTimeout(() => navigate('/login'), 4000);
        }
      } catch (err) {
        setNotification({
          show: true,
          message:
            err.message ||
            'Houve um problema ao validar seu convite. Por favor, tente novamente mais tarde.',
          type: 'error',
        });
        window.setTimeout(() => navigate('/login'), 4000);
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Reset dependent fields when institution type changes
      ...(name === 'id_tipo_instituicao'
        ? { id_area_atuacao: '', id_cargo: '' }
        : {}),
    }));

    if (name === 'id_tipo_instituicao') {
      if (value) {
        getAreasAtuacao(value)
          .then(res => setAreasAtuacao(Array.isArray(res) ? res : []))
          .catch(() => {});
        getCargos(value)
          .then(res => setCargos(Array.isArray(res) ? res : []))
          .catch(() => {});
      } else {
        setAreasAtuacao([]);
        setCargos([]);
      }
    }
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!isValidPassword(formData.password)) {
      setNotification({
        show: true,
        message:
          getPasswordErrorMessage() ||
          'A senha precisa ter no mínimo 8 caracteres, contendo letras e números.',
        type: 'error',
      });
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setNotification({
        show: true,
        message:
          'As senhas informadas não coincidem. Por favor, verifique e tente novamente.',
        type: 'error',
      });
      return;
    }

    if (!formData.acceptedTerms) {
      setNotification({
        show: true,
        message: 'Você precisa ler e aceitar os Termos de Uso para prosseguir.',
        type: 'error',
      });
      return;
    }

    if (!formData.acceptedPrivacy) {
      setNotification({
        show: true,
        message:
          'Você precisa ler e aceitar a Política de Privacidade para prosseguir.',
        type: 'error',
      });
      return;
    }

    setLoading(true);
    setNotification(prev => ({ ...prev, show: false }));

    try {
      const userData = {
        nm_usuario: formData.name,
        nm_email: formData.email,
        pw_senha: formData.password,
        id_instituicao: formData.institutionId,
        sg_aluno: 'S',
        dt_nascimento: formData.birthDate,
        cd_genero: formData.gender,
        bo_liberado: false,
        cd_acesso_instituicao: token,
        id_tipo_instituicao: formData.id_tipo_instituicao
          ? Number(formData.id_tipo_instituicao)
          : null,
        id_area_atuacao: formData.id_area_atuacao
          ? Number(formData.id_area_atuacao)
          : null,
        id_cargo: formData.id_cargo ? Number(formData.id_cargo) : null,
      };

      await createUserMember(userData);

      setSuccessModal({ open: true, email: formData.email });
    } catch (err) {
      setNotification({
        show: true,
        message: err.message || 'Ocorreu um erro durante o cadastro',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className='min-h-screen flex justify-center items-center bg-gray-50'>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className='min-h-screen flex flex-col md:flex-row font-sans text-gray-800 bg-white pt-14 md:pt-16'>
      <NotificationPopup
        isVisible={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      <SignupSuccessModal
        open={successModal.open}
        email={successModal.email}
        onClose={() => navigate('/login')}
      />
      <div
        className='hidden md:flex md:w-1/2 relative flex-col justify-center items-center text-white p-12 overflow-hidden'
        style={{ minHeight: 'calc(100vh - 64px)' }}
      >
        <div
          className='absolute inset-0 z-0'
          style={{
            backgroundImage: `url(${ReatorBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className='absolute inset-0 bg-[#0a2e3d]/40 mix-blend-multiply' />
          <div className='absolute inset-0 bg-gradient-to-tr from-[#0a2e3d] via-transparent to-transparent opacity-40' />
        </div>

        <div className='relative z-10 text-center'>
          <h1 className='text-4xl md:text-5xl font-bold mb-6 leading-tight'>
            Junte-se a nós!
          </h1>
          <p className='text-lg md:text-xl text-gray-200 max-w-md mx-auto'>
            Complete seu cadastro para acessar a plataforma.
          </p>
        </div>
      </div>

      <div className='w-full md:w-1/2 flex items-center justify-center px-5 py-8 md:p-12 relative'>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className='w-full max-w-md'
        >
          <div className='mb-8'>
            <h2 className='text-3xl font-bold text-[#0a2e3d] mb-2'>
              Completar Cadastro
            </h2>
            <p className='text-gray-500'>Confirme seus dados abaixo</p>
          </div>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                E-mail
              </label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                readOnly
                className='w-full px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed'
              />
            </div>

            <div>
              <label
                htmlFor='name'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Nome Completo
              </label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleChange}
                required
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                placeholder='Seu nome completo'
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label
                  htmlFor='birthDate'
                  className='block text-sm font-semibold text-gray-700 mb-2'
                >
                  Data de Nascimento
                </label>
                <input
                  type='date'
                  id='birthDate'
                  name='birthDate'
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                />
              </div>

              <div>
                <label
                  htmlFor='gender'
                  className='block text-sm font-semibold text-gray-700 mb-2'
                >
                  Gênero
                </label>
                <select
                  id='gender'
                  name='gender'
                  value={formData.gender}
                  onChange={handleChange}
                  required
                  className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                >
                  <option value=''>Selecione seu gênero</option>
                  <option value='M'>Masculino</option>
                  <option value='F'>Feminino</option>
                  <option value='O'>Outro</option>
                  <option value='I'>Prefiro não dizer</option>
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor='id_tipo_instituicao'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Tipo de Instituição{' '}
                <span className='text-gray-400 font-normal'>(opcional)</span>
              </label>
              <select
                id='id_tipo_instituicao'
                name='id_tipo_instituicao'
                value={formData.id_tipo_instituicao}
                onChange={handleChange}
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
              >
                <option value=''>Selecione o tipo de instituição</option>
                {tiposInstituicao.map(tipo => (
                  <option
                    key={tipo.id_tipo_instituicao}
                    value={tipo.id_tipo_instituicao}
                  >
                    {tipo.nm_tipo_instituicao}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor='id_cargo'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Cargo{' '}
                <span className='text-gray-400 font-normal'>(opcional)</span>
              </label>
              <select
                id='id_cargo'
                name='id_cargo'
                value={formData.id_cargo}
                onChange={handleChange}
                disabled={!formData.id_tipo_instituicao}
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <option value=''>Selecione seu cargo</option>
                {cargos.map(cargo => (
                  <option key={cargo.id_cargo} value={cargo.id_cargo}>
                    {cargo.nm_cargo}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor='id_area_atuacao'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Atuação{' '}
                <span className='text-gray-400 font-normal'>(opcional)</span>
              </label>
              <select
                id='id_area_atuacao'
                name='id_area_atuacao'
                value={formData.id_area_atuacao}
                onChange={handleChange}
                disabled={!formData.id_tipo_instituicao}
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <option value=''>Selecione sua área de atuação</option>
                {areasAtuacao.map(area => (
                  <option
                    key={area.id_area_atuacao}
                    value={area.id_area_atuacao}
                  >
                    {area.nm_area_atuacao}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor='password'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Senha
              </label>
              <input
                type='password'
                id='password'
                name='password'
                value={formData.password}
                onChange={handleChange}
                required
                minLength='8'
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                placeholder='••••••••'
              />
            </div>

            <div>
              <label
                htmlFor='passwordConfirm'
                className='block text-sm font-semibold text-gray-700 mb-2'
              >
                Confirme sua senha
              </label>
              <input
                type='password'
                id='passwordConfirm'
                name='passwordConfirm'
                value={formData.passwordConfirm}
                onChange={handleChange}
                required
                minLength='8'
                className='w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-200'
                placeholder='••••••••'
              />
            </div>

            <div className='flex items-start space-x-3 mt-4'>
              <input
                type='checkbox'
                id='acceptedTerms'
                name='acceptedTerms'
                checked={formData.acceptedTerms}
                onChange={handleChange}
                required
                className='mt-1 h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500'
              />
              <label
                htmlFor='acceptedTerms'
                className='text-xs text-gray-500 leading-tight'
              >
                Eu li e concordo com os{' '}
                <Link
                  to='/termos'
                  className='text-cyan-600 hover:text-cyan-800 underline transition-colors'
                >
                  Termos de Uso
                </Link>
              </label>
            </div>

            <div className='flex items-start space-x-3 mt-3'>
              <input
                type='checkbox'
                id='acceptedPrivacy'
                name='acceptedPrivacy'
                checked={formData.acceptedPrivacy}
                onChange={handleChange}
                required
                className='mt-1 h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500'
              />
              <label
                htmlFor='acceptedPrivacy'
                className='text-xs text-gray-500 leading-tight'
              >
                Eu li e concordo com as{' '}
                <Link
                  to='/privacidade'
                  className='text-cyan-600 hover:text-cyan-800 underline transition-colors'
                >
                  Políticas de Privacidade
                </Link>
              </label>
            </div>

            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              type='submit'
              disabled={loading}
              className='w-full bg-[#0a2e3d] hover:bg-[#041c26] text-white font-bold py-3.5 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center mt-6 text-lg'
            >
              {loading ? (
                <>
                  <svg
                    className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    ></circle>
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    ></path>
                  </svg>
                  Criando conta...
                </>
              ) : (
                'Criar Conta'
              )}
            </motion.button>
          </form>

          <div className='mt-8 text-center text-sm text-gray-600'>
            Já tem uma conta?{' '}
            <Link
              to='/login'
              className='text-cyan-600 hover:text-cyan-800 font-bold ml-1 transition-colors'
            >
              Faça login
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InviteSignUpPage;
